/**
 * Tab "Correos" del detalle de OT.
 *
 * Lista los correos (entrantes y salientes) vinculados a la OT y permite
 * responder directamente desde aquí. Estilo Zendesk: hilo cronológico,
 * burbujas distintas para inbound/outbound, composer al final.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Mail, Reply, RefreshCw, Paperclip, ArrowDownToLine, ArrowUpFromLine,
  AlertCircle, MailOpen, Send, ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import API from '@/lib/api';

function formatDateTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('es-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function extractEmailAddr(s) {
  if (!s) return '';
  const m = s.match(/<([^>]+)>/);
  return (m ? m[1] : s).trim();
}

export default function OrdenCorreosTab({ orden }) {
  const [items, setItems] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [esperando, setEsperando] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [replyTo, setReplyTo] = useState(null); // mensaje al que se responde
  const [composing, setComposing] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/email/ordenes/${orden.id}/messages`);
      setItems(data.items || []);
      setEsperando(!!data.cliente_esperando_respuesta);
    } catch (e) {
      toast.error('No se pudieron cargar los correos');
    } finally {
      setLoading(false);
    }
  }, [orden.id]);

  useEffect(() => {
    cargar();
    API.get('/email/accounts').then(r => setAccounts(r.data?.accounts || [])).catch(() => {});
  }, [cargar]);

  const handleSync = async () => {
    if (accounts.length === 0) {
      toast.error('Configura una cuenta primero en Configuración › Correo');
      return;
    }
    setSyncing(true);
    try {
      for (const a of accounts) {
        await API.post(`/email/accounts/${a.id}/sync`);
      }
      toast.success('Sincronizado');
      cargar();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error sincronizando');
    } finally {
      setSyncing(false);
    }
  };

  const toggleExpand = async (m) => {
    const newSet = new Set(expandedIds);
    if (newSet.has(m.id)) {
      newSet.delete(m.id);
    } else {
      newSet.add(m.id);
      if (!m.is_read && m.direction === 'inbound') {
        API.post(`/email/messages/${m.id}/read`).catch(() => {});
        setItems(prev => prev.map(x => x.id === m.id ? { ...x, is_read: true } : x));
      }
    }
    setExpandedIds(newSet);
  };

  const handleResponder = (m) => {
    setReplyTo(m);
    setComposing(true);
  };

  const handleNuevoCorreo = () => {
    setReplyTo(null);
    setComposing(true);
  };

  const correosOrdenados = useMemo(() => {
    // El backend ya los devuelve desc; queremos asc cronológico (estilo conversación)
    return [...items].reverse();
  }, [items]);

  return (
    <div className="space-y-4" data-testid="orden-correos-tab">
      {/* Cabecera con estado y acciones */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Mail className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold flex items-center gap-2">
                  Correos vinculados ({items.length})
                  {esperando && (
                    <Badge className="bg-amber-100 text-amber-800 border-amber-300" data-testid="badge-esperando-respuesta">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      Cliente esperando respuesta
                    </Badge>
                  )}
                </h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Hilo de comunicación por email asociado a esta OT
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleSync} disabled={syncing} data-testid="correos-sync">
                <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Sincronizando…' : 'Sincronizar'}
              </Button>
              <Button size="sm" onClick={handleNuevoCorreo} disabled={accounts.length === 0} data-testid="correos-nuevo">
                <Send className="w-3.5 h-3.5 mr-1.5" />
                Nuevo correo
              </Button>
            </div>
          </div>

          {accounts.length === 0 && (
            <div className="mt-3 text-xs bg-amber-50 border border-amber-200 text-amber-800 rounded p-2">
              No hay cuenta de correo configurada.{' '}
              <Link to="/crm/configuracion/email" className="underline font-medium">
                Configurar ahora
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Composer (cuando se responde o se redacta nuevo) */}
      {composing && (
        <CorreoComposer
          accounts={accounts}
          orden={orden}
          replyTo={replyTo}
          onClose={() => { setComposing(false); setReplyTo(null); }}
          onSent={() => { setComposing(false); setReplyTo(null); cargar(); }}
        />
      )}

      {/* Hilo de mensajes */}
      {loading ? (
        <div className="p-8 text-center text-sm text-muted-foreground">Cargando…</div>
      ) : correosOrdenados.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-sm text-muted-foreground">
            <MailOpen className="w-10 h-10 mx-auto mb-2 opacity-30" />
            No hay correos vinculados a esta orden.
            <p className="mt-1 text-xs">
              Los correos del cliente se vinculan automáticamente al sincronizar (por email, teléfono o número de OT).
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {correosOrdenados.map(m => (
            <CorreoItem
              key={m.id}
              mensaje={m}
              expandido={expandedIds.has(m.id)}
              onToggle={() => toggleExpand(m)}
              onResponder={() => handleResponder(m)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CorreoItem({ mensaje, expandido, onToggle, onResponder }) {
  const inbound = mensaje.direction === 'inbound';
  const noLeido = !mensaje.is_read && inbound;

  return (
    <Card
      className={`transition-all ${noLeido ? 'border-blue-500/60 bg-blue-50/30' : ''}`}
      data-testid={`correo-item-${mensaje.id}`}
    >
      <button
        onClick={onToggle}
        className="w-full text-left p-4 flex items-start gap-3 hover:bg-muted/30 transition-colors"
      >
        <div className={`p-2 rounded-lg shrink-0 ${inbound ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
          {inbound ? <ArrowDownToLine className="w-4 h-4" /> : <ArrowUpFromLine className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              {noLeido && <span className="inline-block w-2 h-2 rounded-full bg-blue-500" />}
              <span className="font-medium text-sm">
                {inbound ? mensaje.from : `Para: ${(mensaje.to || []).join(', ')}`}
              </span>
              <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                {inbound ? 'Entrante' : 'Saliente'}
              </Badge>
              {mensaje.attachments?.length > 0 && (
                <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                  <Paperclip className="w-2.5 h-2.5 mr-0.5" />
                  {mensaje.attachments.length}
                </Badge>
              )}
            </div>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {formatDateTime(mensaje.date)}
            </span>
          </div>
          <p className={`text-sm mt-1 ${noLeido ? 'font-semibold' : ''}`}>
            {mensaje.subject || '(sin asunto)'}
          </p>
          {!expandido && (
            <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
              {(mensaje.body_text || '').slice(0, 200)}
            </p>
          )}
        </div>
      </button>

      {expandido && (
        <div className="border-t px-4 pb-4 pt-3 space-y-3">
          <CorreoBody mensajeId={mensaje.id} />
          {mensaje.attachments?.length > 0 && (
            <div className="text-xs flex flex-wrap gap-2">
              {mensaje.attachments.map((a, i) => (
                <Badge key={i} variant="outline" className="gap-1">
                  <Paperclip className="w-3 h-3" />
                  {a.filename} ({Math.round((a.size || 0) / 1024)} KB)
                </Badge>
              ))}
            </div>
          )}
          {inbound && (
            <div className="flex justify-end">
              <Button size="sm" onClick={onResponder} data-testid={`correo-responder-${mensaje.id}`}>
                <Reply className="w-3.5 h-3.5 mr-1.5" />
                Responder
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function CorreoBody({ mensajeId }) {
  const [contenido, setContenido] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    API.get(`/email/messages/${mensajeId}`)
      .then(r => setContenido(r.data))
      .catch(() => setError(true));
  }, [mensajeId]);

  if (error) return <p className="text-xs text-red-600">No se pudo cargar el contenido</p>;
  if (!contenido) return <p className="text-xs text-muted-foreground">Cargando contenido…</p>;

  return (
    <div className="prose prose-sm max-w-none text-sm">
      {contenido.body_html ? (
        <div
          className="email-body border rounded-md p-3 bg-white max-h-[400px] overflow-y-auto"
          dangerouslySetInnerHTML={{ __html: contenido.body_html }}
        />
      ) : (
        <pre className="whitespace-pre-wrap font-sans text-sm border rounded-md p-3 bg-muted/30 max-h-[400px] overflow-y-auto">
          {contenido.body_text || '(sin contenido)'}
        </pre>
      )}
    </div>
  );
}

function CorreoComposer({ accounts, orden, replyTo, onClose, onSent }) {
  const [accountId, setAccountId] = useState(accounts[0]?.id || '');
  const defaultTo = replyTo ? extractEmailAddr(replyTo.from) : (orden?.cliente_email || '');
  const [toInput, setToInput] = useState(defaultTo);
  const [subject, setSubject] = useState(
    replyTo
      ? (replyTo.subject?.startsWith('Re:') ? replyTo.subject : `Re: ${replyTo.subject || ''}`)
      : `[${orden?.numero_orden || ''}] `,
  );
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!accountId && accounts[0]) setAccountId(accounts[0].id);
  }, [accounts, accountId]);

  const handleSend = async () => {
    const toArr = toInput.split(',').map(s => s.trim()).filter(Boolean);
    if (toArr.length === 0) { toast.error('Indica al menos un destinatario'); return; }
    if (!subject.trim()) { toast.error('Asunto requerido'); return; }
    if (!accountId) { toast.error('Selecciona una cuenta'); return; }
    setSending(true);
    try {
      await API.post('/email/send', {
        account_id: accountId,
        to: toArr,
        subject,
        body_text: body,
        body_html: body.replace(/\n/g, '<br>'),
        in_reply_to_id: replyTo?.id,
        orden_id: orden.id,
      });
      toast.success('Correo enviado y vinculado a la OT');
      onSent?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo enviar');
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="border-primary/40" data-testid="correo-composer">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold flex items-center gap-2">
            <Send className="w-4 h-4 text-primary" />
            {replyTo ? 'Responder' : 'Nuevo correo'}
            {replyTo && (
              <Badge variant="outline" className="text-[10px]">
                a: {extractEmailAddr(replyTo.from)}
              </Badge>
            )}
          </h3>
          <Button variant="ghost" size="sm" onClick={onClose}>Cancelar</Button>
        </div>

        {accounts.length > 1 && (
          <div>
            <label className="text-xs text-muted-foreground">Enviar desde</label>
            <select
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              className="w-full rounded border px-3 py-2 text-sm bg-background"
            >
              {accounts.map(a => <option key={a.id} value={a.id}>{a.label} · {a.email}</option>)}
            </select>
          </div>
        )}

        <Input
          placeholder="Para (separar por coma)"
          value={toInput}
          onChange={(e) => setToInput(e.target.value)}
          data-testid="composer-orden-to"
        />
        <Input
          placeholder="Asunto"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          data-testid="composer-orden-subject"
        />
        <Textarea
          placeholder={`Escribe tu mensaje al cliente sobre la OT ${orden.numero_orden}…`}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={8}
          data-testid="composer-orden-body"
        />

        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-[11px] text-muted-foreground">
            Este correo se vinculará automáticamente a la OT {orden.numero_orden}.
            La firma corporativa se añade al final.
          </p>
          <div className="flex gap-2">
            <Link
              to={`/crm/correo`}
              className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1"
            >
              Bandeja general <ExternalLink className="w-3 h-3" />
            </Link>
            <Button onClick={handleSend} disabled={sending || !accountId} data-testid="composer-orden-send">
              <Send className="w-3.5 h-3.5 mr-1.5" />
              {sending ? 'Enviando…' : 'Enviar'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
